import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FlaskConical, 
  ShieldCheck, 
  Truck, 
  Users, 
  ArrowRight, 
  Menu, 
  X,
  ChevronRight,
  Globe,
  FileText,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { publicApi } from '../lib/api';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          publicApi.getCategories(),
          publicApi.getProducts({ limit: 6 })
        ]);
        setCategories(catRes.data || []);
        setProducts(prodRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = [
    { label: 'Products', value: '500+' },
    { label: 'Categories', value: '20+' },
    { label: 'Global Clients', value: '1000+' },
    { label: 'Years Experience', value: '25+' },
  ];

  const features = [
    {
      icon: FlaskConical,
      title: 'Premium Quality',
      description: 'Lab-tested chemicals with purity certifications and complete documentation.'
    },
    {
      icon: ShieldCheck,
      title: 'Safety First',
      description: 'Full SDS documentation, hazard classification, and safe handling guidelines.'
    },
    {
      icon: Truck,
      title: 'Global Delivery',
      description: 'Efficient logistics with real-time tracking and secure packaging.'
    },
    {
      icon: Users,
      title: 'B2B Portal',
      description: 'Dedicated business portal with credit management and bulk ordering.'
    },
  ];

  const getHazardBadgeClass = (hazardClass) => {
    const classes = {
      flammable: 'hazard-flammable',
      corrosive: 'hazard-corrosive',
      toxic: 'hazard-toxic',
      oxidizer: 'hazard-oxidizer',
      irritant: 'hazard-irritant',
      none: 'hazard-none',
    };
    return classes[hazardClass] || 'hazard-none';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <FlaskConical className="w-8 h-8 text-slate-900" />
              <span className="text-xl font-bold tracking-tight text-slate-900">ChemCore</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#products" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Products
              </a>
              <a href="#categories" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Categories
              </a>
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Why Us
              </a>
              <a href="#contact" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Contact
              </a>
            </nav>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="login-button">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="register-button">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-6">
            <nav className="flex flex-col gap-4">
              <a href="#products" className="text-sm font-medium text-slate-600">Products</a>
              <a href="#categories" className="text-sm font-medium text-slate-600">Categories</a>
              <a href="#features" className="text-sm font-medium text-slate-600">Why Us</a>
              <a href="#contact" className="text-sm font-medium text-slate-600">Contact</a>
              <hr className="border-slate-200" />
              <Link to="/login" className="text-sm font-medium text-slate-900">Sign In</Link>
              <Link to="/register">
                <Button className="w-full bg-orange-600 hover:bg-orange-700">Get Started</Button>
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("https://images.pexels.com/photos/5428259/pexels-photo-5428259.jpeg")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <Badge className="mb-6 bg-orange-600/20 text-orange-400 border-orange-600/30">
                Trusted Chemical Supplier
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white mb-6" data-testid="hero-title">
                Industrial Chemicals.<br />
                <span className="text-orange-500">Precision Delivered.</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">
                Premium quality chemicals for manufacturing, research, and industrial applications. 
                Complete with certifications, SDS documentation, and global delivery.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white btn-hover" data-testid="hero-cta">
                    Access B2B Portal
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="#products">
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                    View Products
                  </Button>
                </a>
              </div>
            </div>

            {/* Stats */}
            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <div 
                    key={index}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-md p-6 text-center"
                  >
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Why Choose ChemCore
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Industry-leading standards in quality, safety, and service delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-50 border border-slate-200 rounded-md p-6 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-orange-100 rounded-md flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-2">
                Product Categories
              </h2>
              <p className="text-slate-600">Explore our comprehensive range of chemical products.</p>
            </div>
            <Link to="/register" className="hidden md:flex items-center text-orange-600 hover:text-orange-700 font-medium">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-40 rounded-md"></div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.slice(0, 8).map((category) => (
                <div 
                  key={category.id}
                  className="bg-white border border-slate-200 rounded-md overflow-hidden card-hover group"
                  data-testid={`category-card-${category.id}`}
                >
                  <div className="h-32 bg-slate-100 relative overflow-hidden">
                    {category.image_url && (
                      <img 
                        src={category.image_url} 
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-3">
                      <Badge variant="secondary" className="bg-white/90 text-slate-900">
                        {category.product_count} Products
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{category.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-2">
                Featured Products
              </h2>
              <p className="text-slate-600">High-quality chemicals with complete documentation.</p>
            </div>
            <Link to="/register" className="hidden md:flex items-center text-orange-600 hover:text-orange-700 font-medium">
              View Catalog <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton h-64 rounded-md"></div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div 
                  key={product.id}
                  className="bg-slate-50 border border-slate-200 rounded-md p-6 card-hover"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{product.name}</h3>
                      {product.category_name && (
                        <span className="text-xs text-slate-500">{product.category_name}</span>
                      )}
                    </div>
                    <Badge className={`${getHazardBadgeClass(product.hazard_class)} capitalize text-xs`}>
                      {product.hazard_class === 'none' ? 'Safe' : product.hazard_class}
                    </Badge>
                  </div>

                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{product.description}</p>

                  <div className="space-y-2 mb-4">
                    {product.cas_number && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">CAS Number</span>
                        <span className="font-mono text-slate-700">{product.cas_number}</span>
                      </div>
                    )}
                    {product.purity && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Purity</span>
                        <span className="font-mono text-slate-700">{product.purity}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">MOQ</span>
                      <span className="font-mono text-slate-700">{product.moq} {product.unit}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <div>
                      <span className="text-xs text-slate-500">Starting from</span>
                      <div className="text-xl font-bold text-slate-900">
                        ${product.price.toFixed(2)}
                        <span className="text-sm font-normal text-slate-500">/{product.unit}</span>
                      </div>
                    </div>
                    <Link to="/register">
                      <Button size="sm" variant="outline" className="border-slate-300">
                        Request Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-8">
            Join thousands of businesses that trust ChemCore for their chemical supply needs.
            Access our B2B portal for exclusive pricing and ordering tools.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white btn-hover" data-testid="cta-register">
                Create Business Account
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                Sign In to Portal
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-slate-950 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <FlaskConical className="w-8 h-8 text-orange-500" />
                <span className="text-xl font-bold text-white">ChemCore</span>
              </Link>
              <p className="text-slate-400 max-w-md mb-6">
                Leading supplier of industrial chemicals with a commitment to quality, 
                safety, and customer satisfaction.
              </p>
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Globe className="w-4 h-4" />
                  <span>Global Delivery</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <FileText className="w-4 h-4" />
                  <span>Full Documentation</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <BarChart3 className="w-4 h-4" />
                  <span>B2B Portal</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#products" className="text-slate-400 hover:text-white text-sm">Products</a></li>
                <li><a href="#categories" className="text-slate-400 hover:text-white text-sm">Categories</a></li>
                <li><a href="#features" className="text-slate-400 hover:text-white text-sm">Why Us</a></li>
                <li><Link to="/login" className="text-slate-400 hover:text-white text-sm">B2B Portal</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li>contact@chemcore.com</li>
                <li>+1 (555) 123-4567</li>
                <li>123 Industrial Park,<br />Chemical City, CC 12345</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            &copy; {new Date().getFullYear()} ChemCore. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
